import type {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  MouseEvent as ReactMouseEvent,
} from "react";

import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import type { Point } from "@/lib/geometry";
import {
  TOOL_IDS,
  getDefaultMeasurementTypeForTool,
  getMeasurementToolDefinition,
  type MeasurementType,
  type ToolType,
} from "@/viewport-tools/measurement";
import {
  getMeasurementToolBehavior,
  TOOL_DRAW_RESULT_KINDS,
} from "@/viewport-tools/measurement";

import { measurementRequiresHeight } from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import type { DraftMeasurementState } from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import type { InteractionState } from "@/viewport-tools/measurement/overlay/interactions/types";

interface FinalizeDrawingModeArgs {
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  viewport: DocumentViewportValue;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  setDrawingDraft: Dispatch<SetStateAction<DraftMeasurementState | null>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
  latestDraftRef: MutableRefObject<DraftMeasurementState | null>;
  onDraftMeasurementChange: (draft: DraftMeasurementState | null) => void;
}

export async function finalizeDrawingInteraction({
  drawingDraftRef,
  viewport,
  setErrorMessage,
  setDrawingDraft,
  interactionRef,
  setInteraction,
  latestDraftRef,
  onDraftMeasurementChange,
}: FinalizeDrawingModeArgs) {
  const draft = drawingDraftRef.current;
  if (!draft) {
    return;
  }

  const behavior = getMeasurementToolBehavior(draft.tool);
  const validation = behavior.validateDraft({
    draft,
    pageRectToScreenRect: viewport.transforms.pageRectToScreenRect,
    pageToScreenPoint: viewport.transforms.pageToScreenPoint,
  });

  if (
    measurementRequiresHeight(draft.tool, draft.measurementType) &&
    (!draft.height || draft.height <= 0)
  ) {
    setErrorMessage("Height is required for this measurement");
    return;
  }

  if (!validation.isValid) {
    setErrorMessage(validation.error ?? "Invalid measurement");
    return;
  }

  setErrorMessage(null);
  drawingDraftRef.current = null;
  setDrawingDraft(null);
  interactionRef.current = null;
  setInteraction(null);
  latestDraftRef.current = draft;
  onDraftMeasurementChange(draft);
}

interface UpdateDrawingPreviewModeArgs {
  activeInteraction: Extract<InteractionState, { mode: "drawing" }>;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  snapEnabled: boolean;
  getActiveHeight: () => number | undefined;
  scale: MeasurementScale | null;
  snapSegmentPoint: (anchor: Point, candidate: Point) => Point;
  snapRectangleCorner: (
    anchor: Point,
    candidate: Point,
    lockSquare: boolean
  ) => Point;
  pagePoint: Point;
  shiftKey: boolean;
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  setDrawingDraft: Dispatch<SetStateAction<DraftMeasurementState | null>>;
}

export function updateDrawingPreviewInteraction({
  activeInteraction,
  activeTool,
  activeMeasurementType,
  snapEnabled,
  getActiveHeight,
  scale,
  snapSegmentPoint,
  snapRectangleCorner,
  pagePoint,
  shiftKey,
  drawingDraftRef,
  setDrawingDraft,
}: UpdateDrawingPreviewModeArgs) {
  if (!activeTool) {
    return;
  }

  const behavior = getMeasurementToolBehavior(activeTool);
  const measurementType =
    activeMeasurementType ?? getDefaultMeasurementTypeForTool(activeTool);
  const nextDraft = behavior.buildPreviewDraft({
    vertices: activeInteraction.vertices,
    startPoint: activeInteraction.startPoint,
    pagePoint,
    shiftKey,
    snapEnabled,
    measurementType,
    height: getActiveHeight(),
    scale,
    snapSegmentPoint,
    snapRectangleCorner,
  });
  if (!nextDraft) {
    return;
  }

  drawingDraftRef.current = nextDraft;
  setDrawingDraft(nextDraft);
}

interface HandleDrawingPointerDownModeArgs {
  event: ReactMouseEvent<HTMLDivElement>;
  activeInteraction: InteractionState | null;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  scale: MeasurementScale | null;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  setLastPointerPagePoint: (point: Point) => void;
  onSelectMeasurement: (measurementId: null) => void;
  handleCancelDraft: () => void;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  setDrawingDraft: Dispatch<SetStateAction<DraftMeasurementState | null>>;
  getActiveHeight: () => number | undefined;
  getScreenDistance: (pointA: Point, pointB: Point) => number;
  snapEnabled: boolean;
  snapSegmentPoint: (anchor: Point, candidate: Point) => Point;
  finalizeDrawing: () => Promise<void>;
}

export function handleDrawingPointerDownInteraction({
  event,
  activeInteraction,
  activeTool,
  activeMeasurementType,
  scale,
  getPagePoint,
  setLastPointerPagePoint,
  onSelectMeasurement,
  handleCancelDraft,
  setErrorMessage,
  interactionRef,
  setInteraction,
  drawingDraftRef,
  setDrawingDraft,
  getActiveHeight,
  getScreenDistance,
  snapEnabled,
  snapSegmentPoint,
  finalizeDrawing,
}: HandleDrawingPointerDownModeArgs): boolean {
  if (!activeTool || !scale) {
    return false;
  }
  if (event.button !== 0) {
    return true;
  }

  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return true;
  }
  setLastPointerPagePoint(pagePoint);

  const measurementType =
    activeMeasurementType ?? getDefaultMeasurementTypeForTool(activeTool);
  const draftHeight = getActiveHeight();

  if (measurementRequiresHeight(activeTool, measurementType) && (!draftHeight || draftHeight <= 0)) {
    setErrorMessage(
      `Height must be set for ${getMeasurementToolDefinition(activeTool).label} ${measurementType}`
    );
    return true;
  }

  if (activeTool === TOOL_IDS.rectangle) {
    onSelectMeasurement(null);
    handleCancelDraft();
    setErrorMessage(null);

    const nextInteraction: InteractionState = {
      mode: "drawing",
      startPoint: pagePoint,
      vertices: [pagePoint],
    };
    interactionRef.current = nextInteraction;
    setInteraction(nextInteraction);
    return true;
  }

  const behavior = getMeasurementToolBehavior(activeTool);
  if (!activeInteraction || activeInteraction.mode !== "drawing") {
    onSelectMeasurement(null);
    handleCancelDraft();
    setErrorMessage(null);

    const nextInteraction: InteractionState = {
      mode: "drawing",
      startPoint: pagePoint,
      vertices: [pagePoint],
    };
    interactionRef.current = nextInteraction;
    setInteraction(nextInteraction);

    const initialDraft = behavior.createInitialDraft({
      measurementType,
      startPoint: pagePoint,
      height: draftHeight,
    });
    if (initialDraft) {
      drawingDraftRef.current = initialDraft;
      setDrawingDraft(initialDraft);
    }
    return true;
  }

  const result = behavior.resolveDrawingClick({
    vertices: activeInteraction.vertices,
    pagePoint,
    shiftKey: event.shiftKey,
    snapEnabled,
    measurementType,
    height: getActiveHeight(),
    scale,
    getScreenDistance,
    snapSegmentPoint,
  });
  if (!result || result.kind === TOOL_DRAW_RESULT_KINDS.ignore) {
    return true;
  }

  if (result.kind === TOOL_DRAW_RESULT_KINDS.finalize) {
    drawingDraftRef.current = result.draft;
    void finalizeDrawing();
    return true;
  }

  drawingDraftRef.current = result.draft;
  setDrawingDraft(result.draft);

  const nextInteraction: InteractionState = {
    mode: "drawing",
    startPoint: result.nextStartPoint,
    vertices: result.nextVertices,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
  return true;
}
